import {
  indoorCategoriesItemOffersAtom,
  indoorItemNamesAtom,
  indoorVisibleCategoriesAtom,
  indoorVisibleItemOffersAtom,
} from "@/atoms/indoor";
import { FloatingFullscreenButton } from "@/components";
import { ItemModal } from "@/components/modals/ItemModal";
import { useIndoorMerchantStatus } from "@/hooks/use-indoor-merchant-status";
import { useItemModalParam } from "@/hooks/use-item-modal-param";
import { useCategoryScrollSpy } from "@/hooks/use-category-scroll-spy";
import { filterItemOffersByName } from "@/lib/filter-item-offers-by-name";
import { BasicInfo } from "@/types";
import { useAtomValue } from "jotai";
import { useEffect, useMemo, useRef, useState } from "react";
import { MenuItemModalContent } from "./item/item-modal-content";
import { CategoriesList, CategoriesNavigation, ItemOfferCard } from "@/components/home";
import { Collapse, EmptyState, PageContainer, SearchEmpty, Surface, SurfaceProvider } from "@/components/ui";
import { MenuHeader } from "./menu-header";
import { MenuTopBar } from "./menu-top-bar";

/**
 * Indoor PRODUCT_LIST menu view. Visually identical to delivery ListMenu
 * (header + sticky category nav + scrolled list with active category). No
 * cart/reorder/bottom navbar.
 *
 * Search: with a header term, sticky nav and category sections become a flat
 * filtered item list — same UX as delivery ListMenu.
 */
export function MenuListMenu({ basicInfo }: { basicInfo: BasicInfo }) {
  const itemOffers = useAtomValue(indoorVisibleItemOffersAtom);
  const itemNames = useAtomValue(indoorItemNamesAtom);
  const categoriesItemOffers = useAtomValue(indoorCategoriesItemOffersAtom);
  const availableCategories = useAtomValue(indoorVisibleCategoriesAtom);
  const { merchantIsOpen } = useIndoorMerchantStatus();

  const [itemsFilter, setItemsFilter] = useState("");

  const [isSticky, setIsSticky] = useState(false);

  const { itemOfferId, openItem: handleItemClick, closeItem: handleItemModalClose } =
    useItemModalParam();
  const stickyRef = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLDivElement>(null);
  const itemModalContentRef = useRef<HTMLDivElement>(null);

  const filteredItemOffers = useMemo(() => {
    if (!itemsFilter.trim() || !itemNames) return [];
    return filterItemOffersByName(itemOffers, itemNames, itemsFilter);
  }, [itemsFilter, itemNames, itemOffers]);

  const { categoryInViewId, detectActiveCategory, handleCategoryClick } =
    useCategoryScrollSpy(availableCategories);

  const isSearching = itemsFilter.trim().length > 0;

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsSticky(!entry.isIntersecting);
      },
      { root: null, threshold: 0 },
    );

    const sentinel = stickyRef.current;
    if (sentinel) observer.observe(sentinel);

    return () => {
      if (sentinel) observer.unobserve(sentinel);
    };
  }, [isSearching]);

  useEffect(() => {
    function handleScroll() {
      detectActiveCategory();
    }
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [detectActiveCategory]);

  return (
    <>
      <MenuTopBar
        basicInfo={basicInfo}
        searchValue={itemsFilter}
        onSearchChange={setItemsFilter}
        searchPlaceholder="Buscar produtos..."
      />
      <MenuHeader basicInfo={basicInfo} />
      {!isSearching && (
        <>
          <div ref={stickyRef} className="h-px" />
          <SurfaceProvider surface="card">
            <Surface surface="card" className="sticky top-0 z-sticky-raised mx-auto -mt-1 grid max-w-shell">
              <Collapse open={isSticky}>
                <div className="h-header" />
              </Collapse>
              <PageContainer className="min-w-0">
                <nav ref={navRef} className="flex gap-2 overflow-x-auto scroll-smooth pt-1">
                  {availableCategories.map((c) => (
                    <CategoriesNavigation
                      category={c}
                      categoryInViewId={categoryInViewId}
                      onCategoryClick={handleCategoryClick}
                      key={c.id}
                    />
                  ))}
                </nav>
              </PageContainer>
            </Surface>
          </SurfaceProvider>
        </>
      )}
      <PageContainer gutter="none" surface="card" paint className="pt-4">
        {isSearching ? (
          filteredItemOffers.length === 0 ? (
            <PageContainer>
              <SearchEmpty
                className="min-h-empty justify-center"
                title="Nenhum produto encontrado"
                description={`Não encontramos produtos com o termo "${itemsFilter}".`}
              />
            </PageContainer>
          ) : (
            <PageContainer bottom="chrome" className="grid min-h-fill max-w-shell content-start gap-3">
              {filteredItemOffers.map((itemOffer, index) => (
                <ItemOfferCard
                  key={itemOffer.id}
                  itemOffer={itemOffer}
                  status="AVAILABLE"
                  disabled={itemOffer.status === "UNAVAILABLE"}
                  index={index}
                  itemOffers={filteredItemOffers}
                  handleItemClick={handleItemClick}
                  itemId={itemOffer.id}
                  hideQuantityButton
                />
              ))}
            </PageContainer>
          )
        ) : availableCategories.length === 0 ? (
          <PageContainer>
            <EmptyState
              size="section"
              className="min-h-empty justify-center"
              title={merchantIsOpen ? "Ainda não temos itens disponíveis" : "Cardápio fora do horário"}
              description="Estamos preparando o cardápio. Volte em breve!"
            />
          </PageContainer>
        ) : (
          availableCategories.map((category) => (
            <CategoriesList
              key={category.id}
              category={category}
              isLastItem={
                category.id ===
                availableCategories
                  .filter((c) => categoriesItemOffers[c.id]?.length > 0)
                  .slice(-1)[0]?.id
              }
              offers={categoriesItemOffers[category.id] ?? []}
              onItemClick={handleItemClick}
              hideQuantityButton
            />
          ))
        )}
      </PageContainer>

      <FloatingFullscreenButton />

      <ItemModal
        open={itemOfferId !== ""}
        onClose={handleItemModalClose}
        contentRef={itemModalContentRef}
      >
        <MenuItemModalContent
          onClose={handleItemModalClose}
          itemOfferId={itemOfferId}
          handleItemClick={handleItemClick}
          contentRef={itemModalContentRef}
        />
      </ItemModal>
    </>
  );
}
